import { build_case_form_sections, resolve_gate_id_from_stage_label } from '@/services/onboarding_journey_service';
import { build_historical_manual_review_sections } from '@/views/onboarding_system_views/Gates/Verification/historical_manual_context_helpers';
import {
  build_field_run_by_question,
  decorate_review_form_section
} from '@/views/onboarding_system_views/Gates/Verification/manage_verification_manual_helpers';
import {
  get_check_sort_value,
  merge_selected_check_group
} from '@/views/onboarding_system_views/Gates/Verification/manage_verification_helpers';

const HISTORY_APPLICATION_GATE_IDS = new Set(['onboarding', 'draft', 'submitted', 'approved', 'rejected']);
const HISTORY_SUPPORTING_CONTEXT_GATE_IDS = new Set(['verification', 'approved', 'rejected', 'e-signing']);
const APPLICATION_REFERENCE_TAB_ID = 'application-reference';

function get_gate_transition_log(transition_history = [], gate_id = '') {
  const normalized_gate_id = String(gate_id || '').trim().toLowerCase();
  if (!normalized_gate_id) return null;

  const logs = Array.isArray(transition_history) ? [...transition_history] : [];
  return logs
    .filter((log) => resolve_gate_id_from_stage_label(log?.to_status?.name || '') === normalized_gate_id)
    .sort((left, right) => new Date(right?.date_created || 0).getTime() - new Date(left?.date_created || 0).getTime())[0] || null;
}

function get_first_text(values = [], fallback = '-') {
  const resolved = values.find((value) => String(value || '').trim());
  return String(resolved || fallback).trim() || fallback;
}

export const queueFormHistoryComputed = {
  active_history_step() {
    return this.active_workflow_tab_step && this.active_workflow_tab_step?.state !== 'current'
      && this.active_workflow_tab_step?.gate_id !== APPLICATION_REFERENCE_TAB_ID
      ? this.active_workflow_tab_step
      : null;
  },
  active_history_step_badge_label() {
    return this.active_history_step?.state === 'completed' ? 'Completed' : 'Previous';
  },
  active_history_step_description() {
    return this.active_history_step?.state === 'completed'
      ? 'Read-only view of what was completed in this step before the current gate.'
      : 'Read-only view of this earlier workflow step. This applicant has already moved past this stage, and detailed transition history is not available here yet.';
  },
  onboarding_history_form_sections() {
    return this.hydrated_case_detail ? build_case_form_sections(this.hydrated_case_detail) : [];
  },
  history_manual_field_runs() {
    const field_runs = this.history_manual_run?.field_runs;
    return Array.isArray(field_runs) ? field_runs : [];
  },
  history_manual_review_sections() {
    if (!this.history_manual_run) return [];

    const source_sections = build_historical_manual_review_sections({
      manual_run: this.history_manual_run,
      field_runs: this.history_manual_field_runs,
      manual_binding: {
        manual_verification_form_name: this.history_manual_run?.manual_verification_form_name || 'Manual verification'
      },
      manual_form_sections: [],
      manual_mappings: []
    }, this.onboarding_history_form_sections);

    if (source_sections.length) {
      const field_run_by_question = build_field_run_by_question(this.history_manual_field_runs);
      return source_sections
        .map((form_section) => decorate_review_form_section(form_section, field_run_by_question))
        .filter((form_section) => Array.isArray(form_section?.sections) && form_section.sections.length);
    }

    return [{
      id: String(this.history_manual_run?.id || 'manual-history'),
      form_name: this.history_manual_run?.manual_verification_form_name || 'Manual verification',
      answered_count: this.history_manual_field_runs.length,
      sections: [{
        id: 'manual-history-section',
        label: 'Mapped fields',
        sub_sections: [{
          id: 'manual-history-subsection',
          label: 'Mapped fields',
          questions: this.history_manual_field_runs.map((field_run, index) => ({
            id: field_run?.section_question || field_run?.id || `field-run-${index}`,
            label: field_run?.field_label_snapshot || 'Reviewed field',
            answer: field_run?.user_answer || '',
            detail: field_run?.source_evidence
              ? `Review against submitted value for ${field_run.source_evidence}.`
              : 'Review this manual verification form question.',
            status: String(field_run?.match_status || '').trim().toLowerCase() === 'match'
              ? 'pass'
              : String(field_run?.match_status || '').trim().toLowerCase() === 'mismatch'
                ? 'fail'
                : 'na',
            comment: field_run?.reviewer_comment || '',
            file_url: ''
          }))
        }]
      }]
    }];
  },
  history_documents() {
    return Array.isArray(this.hydrated_case_detail?.documents) ? this.hydrated_case_detail.documents : [];
  },
  history_document_items() {
    return this.history_documents.map((document, index) => ({
      id: document?.id || `history-document-${index}`,
      label: document?.question || document?.section || document?.name || 'Document',
      file_name: document?.file_name || document?.name || 'Uploaded file',
      url: document?.presigned_url || document?.file_url || '',
      status: document?.is_verified
        ? 'Approved'
        : String(document?.verification_state || '').trim().toLowerCase() === 'rejected'
          ? 'Rejected'
          : 'Submitted',
      source: document?.source || 'application',
      application_form_id: document?.application_form_id || 0,
      section_id: document?.section_id || 0,
      question_id: document?.question_id || 0,
      verification_state: document?.verification_state || '',
      verification_comment: document?.verification_comment || '',
      is_verified: document?.is_verified === true,
      content_type: document?.content_type || document?.mime_type || ''
    }));
  },
  history_agreement_items() {
    const agreements = Array.isArray(this.hydrated_case_detail?.agreements) ? this.hydrated_case_detail.agreements : [];
    return agreements.map((agreement, index) => ({
      id: agreement?.id || agreement?.contract_id || `history-agreement-${index}`,
      name: agreement?.document_name || agreement?.title || agreement?.name || 'Agreement',
      status: agreement?.status || agreement?.signing_status || agreement?.state || 'Sent',
      sent_on: agreement?.created_at || agreement?.sent_at || agreement?.updated_at || '',
      signer: agreement?.signatory_name || agreement?.recipient_name || agreement?.signer_name || '',
      url: agreement?.document_url || agreement?.file_url || agreement?.presigned_url || ''
    }));
  },
  active_history_transition_log() {
    return get_gate_transition_log(this.workflow_transition_history, this.active_history_gate_id);
  },
  active_history_action_label() {
    return get_first_text([
      this.active_history_transition_log?.action_taken,
      this.active_history_transition_log?.to_status?.name,
      this.active_history_step?.label
    ], 'Completed');
  },
  active_history_status_label() {
    return get_first_text([
      this.active_history_transition_log?.to_status?.name,
      this.active_history_transition_log?.action_taken,
      this.active_history_step?.label
    ], 'Completed');
  },
  active_history_status_tone() {
    const label = String(this.active_history_status_label || '').trim().toLowerCase();
    if (label.includes('reject')) return 'danger';
    if (label.includes('request') || label.includes('change')) return 'warning';
    if (label.includes('approve') || label.includes('complete') || label.includes('submit') || label.includes('sign')) return 'success';
    return 'secondary';
  },
  active_history_actor() {
    return get_first_text([
      this.active_history_transition_log?.reviewer,
      this.active_history_transition_log?.role
    ], 'System');
  },
  active_history_completed_at() {
    return get_first_text([this.active_history_transition_log?.date_created], '-');
  },
  active_history_reason() {
    return get_first_text([
      this.active_history_transition_log?.note
    ], 'No additional history was recorded for this completed step.');
  },
  should_show_history_application_details() {
    return HISTORY_APPLICATION_GATE_IDS.has(String(this.active_history_gate_id || '').trim().toLowerCase());
  },
  should_show_history_supporting_context() {
    return HISTORY_SUPPORTING_CONTEXT_GATE_IDS.has(String(this.active_history_gate_id || '').trim().toLowerCase());
  },
  history_verification_result_rows() {
    return this.history_verification_checks
      .filter((item) => item?.verification_mode !== 'manual')
      .map((item) => ({
        id: item?.id || `history-verification-${Math.random()}`,
        check: item?.check || 'Verification check',
        status: item?.status || 'Recorded',
        status_tone: item?.status_tone || 'secondary',
        result: this.get_automatic_result_summary(item),
        last_run: item?.last_run || '-',
        run_by: this.get_check_history_run_by(item),
        duration: this.get_check_history_duration(item)
      }));
  },
  history_auto_verification_checks() {
    const grouped_checks = new Map();
    this.history_verification_checks
      .filter((item) => item?.verification_mode !== 'manual')
      .forEach((item) => {
        merge_selected_check_group(grouped_checks, item);
      });

    return Array.from(grouped_checks.values()).sort((left, right) => (
      get_check_sort_value(right) - get_check_sort_value(left)
    ));
  }
};
